import { dataTypeTests, Operator } from "@/types/insights/operators"
import { AdvancedSearchFilter, CaseDetails, DataType, Field } from "@/types"

import { get } from "lodash"
import { SearchClauseParameters } from "@/types/view"
import { CalculationResults, FilteredCalculationResults } from "@/types/insights/calculatedResults"
import { ClientSideCalculations, GroupedSearchResults } from "@/types/insights/clientSideCalculations"
import { LabelFormat, Insight } from "@/types/insights"
import { CaseColumn } from "@/types/caseColumns"
import { DateRange, DateRangeOptions } from "@/types/insights/dateRange"
import moment from "moment"
import { asLocal } from "@/scripts/defaultTimezone"

const addADay = (date: Date) => new Date(date.getTime() + (60 * 60 * 24 * 1000))
const getDynamicDate = (value: any, dynamic: boolean, operator: Operator, params?: SearchClauseParameters) : Date | DateRange | null => {
	if (value instanceof Date) {
		if (operator === Operator.Equals) {
			return {
				start: value,
				end: addADay(value)
			}
		}

		return value
	}

	let currentDate: Date | DateRange | null = null
	if (dynamic) {
		let now = new Date(Date.now())
		currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
		switch (value) {
			case DateRangeOptions.NextWeek:
			case DateRangeOptions.PreviousWeek:
			case DateRangeOptions.CurrentWeek:
				const dayOfWeek = now.getDay()

				// 1 = Monday
				const startDay = params && (params.startDayOfWeek !== undefined)
					? params.startDayOfWeek
					: 1

				let dayChange = startDay - dayOfWeek
				if (dayChange > 0) {
					dayChange -= 7
				}

				if (value === DateRangeOptions.NextWeek) {
					dayChange += 7
				} else if (value === DateRangeOptions.PreviousWeek) {
					dayChange -= 7
				}
				currentDate.setDate(now.getDate() + dayChange)
				break
			case DateRangeOptions.CurrentMonth:
				const dayOfMonth = now.getDate()
				currentDate.setDate(now.getDate() - dayOfMonth + 1)
				break
			case DateRangeOptions.CurrentQuarter:
				const quarter = Math.floor(now.getMonth() / 3) + 1
				currentDate = new Date(now.getFullYear(), (quarter - 1) * 3, 1)
				break
			case DateRangeOptions.YearToDate:
				currentDate = new Date(now.getFullYear(), 0, 1)
				break
			case DateRangeOptions.WeekNumber:
				if (!params?.weekNumber) {
					return null
				} else {
					let now = new Date(Date.now())
					let firstDayOfWeek =
						params?.startDayOfWeek !== null && typeof params?.startDayOfWeek !== "undefined"
							? params?.startDayOfWeek
							: 1
					let jan4 = new Date(now.getFullYear(), 0, 4)
					let correction = jan4.getDay() + 3
					let ordinal = (params?.weekNumber * 7) + firstDayOfWeek - correction

					currentDate = new Date(now.getFullYear(), 0, 1)
					currentDate.setDate(currentDate.getDate() + (ordinal - 1))
				}
				break
			default:
				const days = parseInt(value)
				if (!isNaN(days)) {
					currentDate.setDate(now.getDate() + days)
				}
				break
		}
	} else {
		currentDate = asLocal(value)
	}

	if (operator === Operator.Equals || operator === Operator.OnDay) {
		return {
			start: currentDate,
			end: addADay(currentDate)
		}
	}

	if (operator === Operator.DateRange) {
		switch (value) {
			case DateRangeOptions.YearToDate:
				currentDate = {
					start: currentDate,
					end: new Date(Date.now())
				}
				break
			case DateRangeOptions.WeekNumber:
			case DateRangeOptions.CurrentWeek:
			case DateRangeOptions.PreviousWeek:
			case DateRangeOptions.NextWeek:
				currentDate = {
					start: currentDate,
					end: moment(currentDate).add(1, "week").subtract(1, "millisecond").toDate()
				}
				break
			case DateRangeOptions.CurrentMonth:
				currentDate = {
					start: currentDate,
					end: moment(currentDate).add(1, "month").subtract(1, "millisecond").toDate()
				}
				break
			case DateRangeOptions.CurrentQuarter:
				currentDate = {
					start: currentDate,
					end: moment(currentDate).add(3, "month").subtract(1, "millisecond").toDate()
				}
				break
			case DateRangeOptions.MonthRange:
				if (params!.startMonthRange && params!.endMonthRange) {
					currentDate = {
						start: params!.startMonthRange,
						end: params!.endMonthRange
					}
				} else {
					currentDate = null
				}
				break
			default:
				if (params && params!.startDate && params!.endDate) {
					currentDate = {
						start: params!.startDate,
						end: params!.endDate
					}
				} else {
					currentDate = null
				}
		}
	}

	return currentDate
}

export const filterMixin = {
	methods: {
		/**
		 * Converts an {@link AdvancedSearchFilter} array into an array of functions to test records against.
		 * @param filters The array of all {@link AdvancedSearchFilter filters} chosen by the user which must be converted to functions to test with.
		 * @param currentUserId The ID of the current user.
		 * @returns An array of functions to test {@link CaseDetails records} against each filters' values depending on the chosen {@link Operator operators}.
		 */
		filtersToFunctions (filters: AdvancedSearchFilter[], currentUserId: string) {
			return filters
				.filter(f => f.orFilters || (dataTypeTests[f.column!.fieldDataType!] !== undefined && dataTypeTests[f.column!.fieldDataType!]![f.operator!] !== undefined))
				.map(f => {
					if (f.orFilters) {
						const orFilterFunctions: ((record: CaseDetails) => boolean)[] = this.filtersToFunctions(f.orFilters, currentUserId)
						return (record: CaseDetails) => orFilterFunctions.some(f => f(record))
					} else {
						// the value to test fields against
						let testValue: any = f.value

						var caseColumn: CaseColumn | undefined = f.column

						if (f.column!.fieldDataType === DataType.dateTime) {
							testValue = getDynamicDate(f.value,
								f.dynamicValue === undefined ? false : f.dynamicValue,
								f.operator === undefined ? Operator.Equals : f.operator,
								f.searchClauseParameters)
						} else if (f.column!.fieldDataType === DataType.timeSpentAsValue) {
							if (f.searchClauseParameters && f.searchClauseParameters.timeSpentClause) {
								testValue = {
									hours: f.searchClauseParameters.timeSpentClause.hoursSpent,
									searchValueLabel: f.searchClauseParameters.timeSpentClause.searchLabel
								}
							} else {
								return (record: CaseDetails) => true
							}
						} else if (f.column!.fieldDataType === DataType.userRole) {
							// userFieldData will be null if not filtering by subrole also
							if (f.userFieldData) {
								caseColumn = f.userFieldData
								testValue = f.value ? f.value.toLocaleLowerCase() : f.value
							}
							if (f.value) {
								testValue = f.value === "[Me]" ? currentUserId : f.value.toLocaleLowerCase()
							}
						} else if (f.column!.fieldDataType === DataType.auditUser) {
							// userFieldData will be null if not filtering by subrole also
							if (f.value) {
								testValue = f.value === "[Me]" ? currentUserId : f.value.toLocaleLowerCase()
							}
						} else if (f.dynamicValue && f.value === "[Me]") {
							testValue = currentUserId
						} else if (f.column!.fieldDataType === DataType.string) {
							testValue = f.value ? f.value.toLocaleLowerCase() : f.value
						}

						const dataTypeOperator = dataTypeTests[caseColumn!.fieldDataType!]![f.operator!]!
						return (record: CaseDetails) => {
							// if there are no fields it is likely because the all the record's user-defined fields in the 'show columns' are null
							// in this case, filter the same way as the server side treats a null field value - depending on the operator
							// e.g. Any(null) - don't match, Contains(null) - don't match, NotEqual(null) - match, etc
							if (caseColumn!.dynamicField && !record.fields) {
								if (f.operator! === Operator.Any ||
									f.operator! === Operator.Contains ||
									f.operator! === Operator.Equals ||
									f.operator! === Operator.GreaterThan ||
									f.operator! === Operator.LessThan ||
									f.operator! === Operator.OnDay ||
									f.operator! === Operator.Within ||
									f.operator! === Operator.InTeam ||
									f.operator! === Operator.IsSet) {
									return false
								} else if (f.operator! === Operator.NotSet && caseColumn!.fieldDataType! === DataType.subrole) {
									// messy but necessary edge-case handling for subrole filtering of user-role fields
									// otherwise returns true when the record has no field of the user-role type whatsoever
									// but we are specifying that the user-role field must exist for the record, but the subrole must not
									return false
								} else {
									return true
								}
							}

							var fieldValue = null
							if (caseColumn!.fieldDataType && caseColumn!.fieldDataType! === DataType.subrole) {
								if (!record.fields[caseColumn!.fieldId!] || !record.fields[caseColumn!.fieldId!].value) {
									fieldValue = null
								} else {
									fieldValue = record.fields[caseColumn!.fieldId!].value.subRoles.find((sr: any) => sr.title && sr.title === caseColumn!.key).users
								}
							} else if (caseColumn!.fieldDataType && caseColumn?.fieldDataType === DataType.auditUser) {
								fieldValue = (record as any)[caseColumn!.key!]?.id ?? null
							} else if (caseColumn!.dynamicField) {
								const fieldId = caseColumn!.key && caseColumn!.key.startsWith("calculation.")
									? caseColumn!.key
									: caseColumn!.fieldId!
								fieldValue = record.fields[fieldId] ? record.fields[fieldId].value : null
							} else {
								fieldValue = (record as any)[caseColumn!.key!]
							}

							return dataTypeOperator(testValue, fieldValue)
						}
					}
				})
		},
		filterList (list: any[], filters: AdvancedSearchFilter[]) {
			return list.filter((item: any) => filters.every((filter: AdvancedSearchFilter) => {
				const filterKey = (filter.filterKey ? filter.filterKey : filter.key) +
					(filter.column && filter.column.dynamicField ? ".value" : "")

				const filterFieldValue = get(item, filterKey)

				switch (filter.operator) {
					case Operator.Equals:
						return filterFieldValue == filter.value // eslint-disable-line
					case Operator.Contains:
						if (filter.columnDataType === "list-multiple") {
							return filterFieldValue && Array.isArray(filterFieldValue) && filterFieldValue.find(i => i.id ? i.id === filter.value : i === filter.value)
						} else if (filter.columnDataType === "list-data") {
							return filterFieldValue && Array.isArray(filterFieldValue) && filterFieldValue.find(i => i.id === filter.value)
						} else {
							return filterFieldValue && filterFieldValue.toLowerCase().indexOf(filter.value!.toLowerCase()) > -1
						}
					case Operator.GreaterThan: return filterFieldValue != null && filterFieldValue > filter.value!
					case Operator.LessThan: return filterFieldValue != null && filterFieldValue < filter.value!
					case Operator.IsSet:
						if (filter.columnDataType === "list-multiple" || filter.columnDataType === "list-data") {
							return !!filterFieldValue && Array.isArray(filterFieldValue) && filterFieldValue.length > 0
						} else {
							return !!filterFieldValue
						}

					case Operator.NotSet:
						if (filter.columnDataType === "list-multiple" || filter.columnDataType === "list-data") {
							return !filterFieldValue || !Array.isArray(filterFieldValue) || filterFieldValue.length === 0
						} else {
							return !filterFieldValue
						}
				}

				return true
			}))
		},
		filterGroups (resultGroups: readonly CalculationResults[], filterFunctions: ((record: CaseDetails) => boolean)[], labelFormat: LabelFormat, insight: Insight, fields: Field[]) : FilteredCalculationResults[] {
			if (!resultGroups) {
				return []
			}

			const unfilteredLengths: { [key: string]: number } = (resultGroups as CalculationResults[]).toDictionaryMapValue(c => c.value, c => c.results ? c.results.length : 0)

			if (!filterFunctions || filterFunctions.length === 0) {
				return resultGroups.map(resultGroup => {
					return {
						...resultGroup,
						unfilteredLength: unfilteredLengths[resultGroup.value]
					}
				})
			}

			const clientFilteredResults: CalculationResults[] =
				resultGroups.map(resultGroup => {
					return {
						...resultGroup,
						results: resultGroup.results
							? resultGroup.results.filter((record: CaseDetails) => filterFunctions.every(f => f(record)))
							: []
					}
				})

			const clientGroupedSearchResults: GroupedSearchResults = {
				labelFormat: labelFormat,
				results: clientFilteredResults.toDictionaryMapValue(c => c.value, c => c.results),
				labels: clientFilteredResults.toDictionaryMapValue(c => c.value, c => c.label)
			}

			const calculationRunner = new ClientSideCalculations()
			const clientCalculatedResults =	calculationRunner.runCalculations(clientGroupedSearchResults, insight, fields)

			calculationRunner.applyCalculationTotals(clientCalculatedResults, insight, [])
			return clientCalculatedResults.results.map(resultGroup => {
				return {
					...resultGroup,
					unfilteredLength: unfilteredLengths[resultGroup.value]
				}
			})
		}
	}
}
